/**
 * @fileoverview Conflict-check party schedule helpers (MOR-992)
 *
 * Purpose: the shapes and pure derivations the party-schedule components
 * share — resolving a party group to per-party rows (older checks only
 * carry names) and tallying PMSI / non-PMSI registrations for the group
 * header. Row flattening lives in the shared `conflictBriefFormat` module so
 * the vote email lays its table out the same way.
 *
 * Used by:
 *  - conflict-party-schedule.tsx
 *  - conflict-party-group.tsx
 */

import type { ConflictScheduledParty } from "@cvx/preAppointment/conflictChecks/schema";

export interface ConflictPartyListView {
  label: string;
  names: string[];
  parties?: ConflictScheduledParty[];
}

/**
 * Per-party rows for a group. Checks snapshotted before MOR-992 carry only
 * `names`, which become registration-less parties.
 */
export function resolveParties(
  list: ConflictPartyListView,
): ConflictScheduledParty[] {
  return list.parties ?? list.names.map((name) => ({ name }));
}

export interface RegistrationTally {
  pmsi: number;
  nonPmsi: number;
}

/** PMSI vs general registrations across a group. Both zero = no PPSR detail. */
export function tallyRegistrations(
  parties: ConflictScheduledParty[],
): RegistrationTally {
  return parties.reduce<RegistrationTally>(
    (tally, party) =>
      (party.registrations ?? []).reduce(
        (inner, registration) =>
          registration.pmsi
            ? { ...inner, pmsi: inner.pmsi + 1 }
            : { ...inner, nonPmsi: inner.nonPmsi + 1 },
        tally,
      ),
    { pmsi: 0, nonPmsi: 0 },
  );
}
